import { AnimatePresence, motion } from "framer-motion";
import { Ref } from "react";
import { useTranslation } from "react-i18next";
import { SearchInputText } from "../../../../../common/components/InputText";
import CompanyOptionTemplate from "./CompanyOptionTemplate";
import LocationList from "./LocationList";

export interface PanelTreeNode {
    children?: PanelTreeNode[];
    city?: string;
    id: string;
    isActive?: boolean;
    isExpanded?: boolean;
    locationType?: string;
    name: string;
}

interface PanelTemplateProps {
    hasSingleCompany: boolean;
    onCompanySelect: (companyId: string) => void;
    onLocationSelect: (companyId: string, locationId: string) => void;
    onSearchChange: (value: string) => void;
    onToggleExpand: (companyId: string) => void;
    options: PanelTreeNode[];
    scrollContainerRef: Ref<HTMLDivElement>;
    search: string;
    searchInputRef: Ref<HTMLInputElement>;
    selectedCompanyId?: string;
    selectedLocationId?: string;
}

const PanelTemplate = (props: PanelTemplateProps) => {
    const {
        hasSingleCompany,
        onCompanySelect,
        onLocationSelect,
        onSearchChange,
        onToggleExpand,
        options,
        scrollContainerRef,
        search,
        searchInputRef,
        selectedCompanyId,
        selectedLocationId,
    } = props;

    const { t } = useTranslation("common");

    // Callback ref to scroll the active node into view once layout is loaded
    const activeNodeRef = (node: HTMLDivElement | null) => {
        if (node) {
            requestAnimationFrame(() => {
                node.scrollIntoView({ block: "center", behavior: "auto" });
            });
        }
    };

    const isCompanyActive = (companyId: string) =>
        selectedCompanyId === companyId && !selectedLocationId;

    const isLocationActive = (companyId: string, locationId: string) =>
        selectedCompanyId === companyId && selectedLocationId === locationId;

    const activeNodeKey = selectedCompanyId
        ? selectedLocationId
            ? `location-${selectedCompanyId}-${selectedLocationId}`
            : `company-${selectedCompanyId}`
        : null;

    return (
        <div>
            <div className="mb-1 border-b border-surface-545 pb-1.5">
                <SearchInputText
                    ref={searchInputRef}
                    className="w-full"
                    containerClassName="w-full"
                    isAnimated={false}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </div>
            <div
                ref={scrollContainerRef}
                className="max-h-[50vh] overflow-y-auto"
            >
                {options.map((company, index) => (
                    <div key={`node-template-${company.id}`}>
                        <CompanyOptionTemplate
                            company={company}
                            isSelected={isCompanyActive(company.id)}
                            isSingleCompany={hasSingleCompany}
                            ref={
                                activeNodeKey === `company-${company.id}`
                                    ? activeNodeRef
                                    : null
                            }
                            onSelect={onCompanySelect}
                            onToggleExpand={onToggleExpand}
                        />
                        {hasSingleCompany ? (
                            <>
                                <div className="mx-1.5 my-1 h-px bg-surface-545" />
                                <LocationList
                                    company={company}
                                    activeNodeKey={activeNodeKey}
                                    activeNodeRef={activeNodeRef}
                                    isLocationActive={isLocationActive}
                                    onLocationSelect={onLocationSelect}
                                />
                            </>
                        ) : (
                            <AnimatePresence initial={false}>
                                {company.isExpanded && (
                                    <motion.div
                                        className="ml-3 overflow-hidden pl-1.5"
                                        initial={{
                                            height: 0,
                                            marginBottom: 0,
                                            opacity: 0,
                                        }}
                                        animate={{
                                            height: "auto",
                                            marginBottom:
                                                index < options.length - 1
                                                    ? 8
                                                    : 0,
                                            opacity: 1,
                                        }}
                                        exit={{
                                            height: 0,
                                            marginBottom: 0,
                                            opacity: 0,
                                        }}
                                        transition={{
                                            duration: 0.2,
                                            ease: "easeInOut",
                                        }}
                                    >
                                        <LocationList
                                            company={company}
                                            activeNodeKey={activeNodeKey}
                                            activeNodeRef={activeNodeRef}
                                            isLocationActive={isLocationActive}
                                            onLocationSelect={onLocationSelect}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        )}
                    </div>
                ))}
                {options.length === 0 && (
                    <div className="flex justify-center items-center h-10">
                        {t("common:noResultsFound")}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PanelTemplate;
